import React from 'react';
import { useTranslation } from '../../contexts/LanguageContext';

// Grid line style presets
const LINE_STYLES = [
  { id: 'solid', name: 'Solid', value: 'solid' },
  { id: 'dashed', name: 'Dashed', value: 'dashed' },
  { id: 'dotted', name: 'Dotted', value: 'dotted' }
];

const LINE_THICKNESSES = [
  { id: 'thin', name: 'Thin', value: '1px' },
  { id: 'medium', name: 'Medium', value: '2px' },
  { id: 'thick', name: 'Thick', value: '3px' }
];

const GridLineSelector = ({
  gridSettings,
  setGridSettings
}) => {
  const { t } = useTranslation();

  // Default settings if not provided
  const settings = {
    enabled: false,
    innerGrid: true,      // Grid lines around each square
    outerBorder: true,    // Border around the entire board
    color: '#000000',
    thickness: 'thin',
    style: 'solid',
    ...gridSettings
  };

  const updateSetting = (key, value) => {
    setGridSettings({
      ...settings,
      [key]: value
    });
  };

  return (
    <section className="settings-section grid-line-section">
      <h3>📐 {t('settings.gridLines', 'Grid Lines')}</h3>
      
      {/* Enable/Disable Toggle */}
      <div className="grid-toggle">
        <label className="toggle-label">
          <span>{t('settings.showGridLines', 'Show Grid Lines')}</span>
          <div className="toggle-switch">
            <input
              type="checkbox"
              checked={settings.enabled}
              onChange={(e) => updateSetting('enabled', e.target.checked)}
              data-testid="grid-lines-toggle"
            />
            <span className="toggle-slider"></span>
          </div>
        </label>
      </div>

      {settings.enabled && (
        <>
          {/* Grid Type Options */}
          <div className="grid-type-options">
            <h4>{t('settings.gridType', 'Grid Type')}:</h4>
            <div className="checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={settings.innerGrid}
                  onChange={(e) => updateSetting('innerGrid', e.target.checked)}
                  data-testid="inner-grid-toggle"
                />
                <span className="checkmark"></span>
                <span>{t('settings.innerGrid', 'Inner Grid (around each square)')}</span>
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={settings.outerBorder}
                  onChange={(e) => updateSetting('outerBorder', e.target.checked)}
                  data-testid="outer-border-toggle"
                />
                <span className="checkmark"></span>
                <span>{t('settings.outerBorder', 'Outer Border (board edge)')}</span>
              </label>
            </div>
          </div>

          {/* Color Picker */}
          <div className="grid-color-picker">
            <h4>{t('settings.lineColor', 'Line Color')}:</h4>
            <div className="color-picker-item">
              <input
                type="color"
                value={settings.color}
                onChange={(e) => updateSetting('color', e.target.value)}
                data-testid="grid-color-picker"
              />
              <span className="color-value">{settings.color}</span>
            </div>
          </div>

          {/* Thickness Options */}
          <div className="grid-thickness">
            <h4>{t('settings.lineThickness', 'Line Thickness')}:</h4>
            <div className="option-buttons">
              {LINE_THICKNESSES.map(thickness => (
                <button
                  key={thickness.id}
                  className={`option-btn ${settings.thickness === thickness.id ? 'active' : ''}`}
                  onClick={() => updateSetting('thickness', thickness.id)}
                  data-testid={`thickness-${thickness.id}`}
                >
                  {t(`settings.thickness${thickness.name}`, thickness.name)}
                </button>
              ))}
            </div>
          </div>

          {/* Style Options */}
          <div className="grid-style">
            <h4>{t('settings.lineStyle', 'Line Style')}:</h4>
            <div className="option-buttons">
              {LINE_STYLES.map(style => (
                <button
                  key={style.id}
                  className={`option-btn ${settings.style === style.id ? 'active' : ''}`}
                  onClick={() => updateSetting('style', style.id)}
                  data-testid={`style-${style.id}`}
                >
                  {t(`settings.style${style.name}`, style.name)}
                </button>
              ))}
            </div>
          </div>

          {/* Preview */}
          <div className="grid-preview">
            <h4>{t('settings.preview', 'Preview')}:</h4>
            <div 
              className="grid-preview-board"
              style={{
                '--grid-color': settings.color,
                '--grid-thickness': LINE_THICKNESSES.find(t => t.id === settings.thickness)?.value || '1px',
                '--grid-style': settings.style
              }}
              data-testid="grid-preview"
            >
              {/* Static 4x4 preview grid — index is stable positional identity */}
              {[...Array(16)].map((_, i) => (
                <div 
                  key={`grid-preview-square-${i}`} 
                  className={`preview-square ${i % 2 === (Math.floor(i / 4) % 2) ? 'light' : 'dark'}`}
                  style={{
                    borderColor: settings.innerGrid ? settings.color : 'transparent',
                    borderWidth: settings.innerGrid ? (LINE_THICKNESSES.find(t => t.id === settings.thickness)?.value || '1px') : '0',
                    borderStyle: settings.innerGrid ? settings.style : 'none'
                  }}
                />
              ))}
              {settings.outerBorder && (
                <div 
                  className="preview-outer-border"
                  style={{
                    borderColor: settings.color,
                    borderWidth: LINE_THICKNESSES.find(t => t.id === settings.thickness)?.value || '1px',
                    borderStyle: settings.style
                  }}
                />
              )}
            </div>
          </div>
        </>
      )}
    </section>
  );
};

export default GridLineSelector;
